import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { Icon, Text, Link, Button, ButtonAppearance } from '@components/shared';
import ModalDialog from '@atlaskit/modal-dialog';

export interface NotificationItemType {
  id: number;
  title: string;
  description: string;
  resourceLink: string;
  date: string;
}

export interface ContentVersioningDialogProps {
  /** The title of the learning resource that has been updated */
  resourceTitle: string;
  /** The modal subtitle */
  subtitle: string;
  /** Notification list */
  notifications: NotificationItemType[];
  /** Callback function called when the modal dialog is requesting to be closed */
  closeModal: () => void;
  /** Callback function called when the resource link is clicked */
  onResourceLinkClick: (resourceLink: string) => void;
}

/**
 * @param {ContentVersioningDialog} ContentVersioningDialogProps
 * @returns {React.ReactElement} React.ReactElement
 *
 * @example
 * <ContentVersioningDialog resourceTitle='Test Resource' notificationDate='March 15, 2024' notificationDescription='Test description' notificationTitle='Test Update' link='https://test.com'  />
 */

export const ContentVersioningDialog: React.FC<ContentVersioningDialogProps> = ({
  resourceTitle,
  subtitle,
  notifications,
  onResourceLinkClick,
  closeModal,
}) => {
  const handleDismiss = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    closeModal();
  };

  const handleResourceClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, resourceLink: string) => {
    e.preventDefault();
    onResourceLinkClick(resourceLink);
  };

  return (
    <ModalDialog onClose={closeModal}>
      <div data-testid="content-versioning-dialog" css={containerStyles}>
        <div css={crossIconContainerStyles}>
          <Button
            label=""
            appearance={ButtonAppearance.ICON}
            iconType="cross"
            iconBefore={<Icon type="cross" color={token('color.icon')} />}
            onClick={handleDismiss}
          />
        </div>
        <div>
          <div data-testid="dialog-title" css={dialogTitleStyles}>
            Update to {resourceTitle}
          </div>
          <div data-testid="dialog-subtitle" css={dialogSubtitleStyles}>
            <Text size="small" color={token('color.text')}>
              {subtitle}
            </Text>
          </div>
          <div>
            <Text size="small" color={token('color.text')}>
              Summary of changes:
            </Text>
            {!!notifications.length &&
              notifications.map((notification) => (
                <div css={notificationContainerStyles} key={notification.id}>
                  <div>
                    <div data-testid="notification-title">
                      <Text>{notification.title}</Text>
                    </div>
                    <span css={notificationDateStyles}>{notification.date}</span>
                  </div>
                  <div css={notificationDescriptionContainerStyles}>
                    <Text size="small" color={token('color.text.subtle')}>
                      {notification.description}
                    </Text>
                  </div>
                  <div css={linkContainerStyles}>
                    <Link
                      target="_blank"
                      label="View changes"
                      link={notification.resourceLink}
                      icon="arrow-right"
                      onClick={(e) => handleResourceClick(e, notification.resourceLink)}
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
        <div css={buttonContainerStyles}>
          <Button label="Dismiss" appearance={ButtonAppearance.PRIMARY} onClick={handleDismiss} />
        </div>
      </div>
    </ModalDialog>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  padding: token('space.300'),
  backgroundColor: token('color.background.input'),
  boxShadow: token('elevation.shadow.raised'),
  borderRadius: '8px',
});

const crossIconContainerStyles = css({
  alignSelf: 'flex-end',
  marginBottom: token('space.150'),
});

const dialogTitleStyles = css({
  marginBottom: token('space.100'),
  color: token('color.text'),
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '24px',
  fontWeight: 600,
  lineHeight: '32px',
  textAlign: 'left',
});

const dialogSubtitleStyles = css({
  marginBottom: token('space.300'),
});

const notificationContainerStyles = css({
  border: `1px solid ${token('color.border')}`,
  padding: `${token('space.150')} ${token('space.200')}`,
  marginTop: token('space.100'),
  borderRadius: '4px',
  backgroundColor: token('color.background.input'),
  borderLeft: `4px solid ${token('color.background.accent.blue.subtlest.pressed')}`,
});

const buttonContainerStyles = css({
  alignSelf: 'flex-end',
  marginTop: token('space.400'),

  a: {
    fontFamily: 'SF Pro, sans-serif',
    fontSize: '14px',
    fontWeight: 500,
    paddingTop: token('space.075'),
    paddingBottom: token('space.075'),
  },
});

const notificationDateStyles = css({
  color: token('color.text.subtlest'),
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  lineHeight: '18px',
});

const notificationDescriptionContainerStyles = css({
  marginTop: token('space.100'),
  marginBottom: token('space.150'),
});

const linkContainerStyles = css({
  a: {
    '&:hover': {
      textDecoration: 'none',
    },
  },
});
